import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  disable: (id) => api.patch(`/products/${id}/disable`),
  enable: (id) => api.patch(`/products/${id}/enable`),
  exportCsv: () => api.get('/products/export', { responseType: 'blob' }),
  importCsv: (csv) => api.post('/products/import', { csv }),
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getMe: () => api.get('/clients/me'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  disable: (id) => api.patch(`/clients/${id}/disable`),
  enable: (id) => api.patch(`/clients/${id}/enable`),
  exportCsv: () => api.get('/clients/export', { responseType: 'blob' }),
  importCsv: (csv) => api.post('/clients/import', { csv }),
};

// Client Groups API
export const clientGroupsAPI = {
  getAll: (params) => api.get('/client-groups', { params }),
  getById: (id) => api.get(`/client-groups/${id}`),
  create: (data) => api.post('/client-groups', data),
  update: (id, data) => api.put(`/client-groups/${id}`, data),
  disable: (id) => api.patch(`/client-groups/${id}/disable`),
  enable: (id) => api.patch(`/client-groups/${id}/enable`),
  exportCsv: () => api.get('/client-groups/export', { responseType: 'blob' }),
  importCsv: (csv) => api.post('/client-groups/import', { csv }),
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  cancel: (id) => api.patch(`/orders/${id}/cancel`),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
  exportCsv: (params) => api.get('/orders/export', { params, responseType: 'blob' }),
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get('/stats'),
};

// Events API
export const eventsAPI = {
  getAll: (params) => api.get('/events', { params }),
};

// Settings API
export const settingsAPI = {
  get: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
};

// Catalog API
export const catalogAPI = {
  exists: () => api.get('/catalog/exists'),
  download: () => api.get('/catalog', { responseType: 'blob' }),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalog', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;
